/**
 * Computes side-by-side layout for overlapping events.
 * Only groups events that share at least one member AND overlap in time.
 * Returns a Map of event.id -> { column, totalColumns }
 */
export function computeOverlapLayout(events, timeToMinutes) {
  if (!events || events.length === 0) return new Map();

  // Sort by start time, then by end time (longer events first)
  const sorted = [...events].sort((a, b) => {
    const aStart = timeToMinutes(a.startTime);
    const bStart = timeToMinutes(b.startTime);
    if (aStart !== bStart) return aStart - bStart;
    return timeToMinutes(b.endTime) - timeToMinutes(a.endTime);
  });

  function sharesMembers(eventA, eventB) {
    const membersA = eventA.members_id || [];
    const membersB = eventB.members_id || [];
    if (membersA.length === 0 && membersB.length === 0) return false;
    return membersA.some((id) => membersB.includes(id));
  }

  function timeOverlaps(a, b) {
    return timeToMinutes(a.startTime) < timeToMinutes(b.endTime) &&
           timeToMinutes(a.endTime) > timeToMinutes(b.startTime);
  }

  // Build overlap groups using union-find approach
  const parent = sorted.map((_, i) => i);
  function find(x) {
    while (parent[x] !== x) { parent[x] = parent[parent[x]]; x = parent[x]; }
    return x;
  }
  function union(a, b) { parent[find(a)] = find(b); }

  for (let i = 0; i < sorted.length; i++) {
    for (let j = i + 1; j < sorted.length; j++) {
      if (timeOverlaps(sorted[i], sorted[j]) && sharesMembers(sorted[i], sorted[j])) {
        union(i, j);
      }
    }
  }

  // Collect groups
  const groupMap = {};
  for (let i = 0; i < sorted.length; i++) {
    const root = find(i);
    if (!groupMap[root]) groupMap[root] = [];
    groupMap[root].push(i);
  }

  // Assign columns within each group
  const layoutMap = new Map();

  for (const group of Object.values(groupMap)) {
    if (group.length === 1) {
      layoutMap.set(sorted[group[0]].id, { column: 0, totalColumns: 1 });
      continue;
    }

    // Greedy column assignment
    const columns = []; // columns[col] = end time of last event in that column
    const assignments = [];

    for (const idx of group) {
      const event = sorted[idx];
      const start = timeToMinutes(event.startTime);

      let placed = false;
      for (let col = 0; col < columns.length; col++) {
        if (start >= columns[col]) {
          columns[col] = timeToMinutes(event.endTime);
          assignments.push({ idx, col });
          placed = true;
          break;
        }
      }

      if (!placed) {
        assignments.push({ idx, col: columns.length });
        columns.push(timeToMinutes(event.endTime));
      }
    }

    const totalColumns = columns.length;
    for (const { idx, col } of assignments) {
      layoutMap.set(sorted[idx].id, { column: col, totalColumns });
    }
  }

  return layoutMap;
}
